import { useContext } from 'react';
import { AuthContext, AuthContextType } from '../contexts/AuthContext';
import permissionService from '../services/permissionService';
import { PERMISSIONS, ROLES, PermissionType, RoleType } from '../constants/permissions';

interface UseAuthReturn extends AuthContextType {
  hasPermission: (permission: PermissionType | string) => boolean;
  hasRoleLevel: (requiredRole: RoleType | string) => boolean;
  isGlobalAdmin: () => boolean;
  isAdmin: () => boolean;
  canAccessClientResource: (resourceClientId: number | string) => boolean;
  canManageClients: () => boolean;
  canManageUsers: () => boolean;
  canManageChatbots: () => boolean;
  canAccessGoogleIntegrations: () => boolean;
  canManageWhatsApp: () => boolean;
  canManageWhatsAppTemplates: () => boolean;
  canManageAutomations: () => boolean;
}

export const useAuth = (): UseAuthReturn => {
  const context = useContext(AuthContext);

  if (!context) {
    throw new Error('useAuth deve essere usato dentro AuthProvider');
  }

  const { user, role, clientId, selectedClientId, setSelectedClientId, isAuthenticated, isLoading, sessionExpired, setUser, clearUser, refreshUser, clearSessionExpired } = context;

  const hasPermission = (permission: PermissionType | string): boolean => {
    if (!role) return false;
    return permissionService.hasPermission(role, permission);
  };

  const hasRoleLevel = (requiredRole: RoleType | string): boolean => {
    if (!role) return false;
    return permissionService.hasRoleLevel(role, requiredRole);
  };

  const isGlobalAdmin = (): boolean => {
    if (!role) return false;
    return permissionService.isGlobalAdmin(role);
  };

  const isAdmin = (): boolean => {
    if (!role) return false;
    return permissionService.isAdmin(role);
  };

  const canAccessClientResource = (resourceClientId: number | string): boolean => {
    if (!role) return false;
    // admin_globale has clientId=null but can access all client resources
    if (permissionService.isGlobalAdmin(role)) return true;
    if (clientId === null) return false;
    return permissionService.canAccessClientResource(role, clientId, resourceClientId);
  };

  const canManageClients = (): boolean => hasPermission(PERMISSIONS.MANAGE_CLIENTS);
  const canManageUsers = (): boolean => hasPermission(PERMISSIONS.MANAGE_USERS);
  const canManageChatbots = (): boolean => hasPermission(PERMISSIONS.MANAGE_CHATBOTS);
  const canAccessGoogleIntegrations = (): boolean => hasPermission(PERMISSIONS.GOOGLE_INTEGRATIONS);
  const canManageWhatsApp = (): boolean => hasPermission(PERMISSIONS.MANAGE_WHATSAPP);
  const canManageWhatsAppTemplates = (): boolean => hasPermission(PERMISSIONS.MANAGE_WHATSAPP_TEMPLATES);
  const canManageAutomations = (): boolean => hasPermission(PERMISSIONS.MANAGE_AUTOMATIONS);

  return {
    user,
    role,
    clientId,
    selectedClientId,
    setSelectedClientId,
    isAuthenticated,
    isLoading,
    sessionExpired,

    setUser,
    clearUser,
    refreshUser,
    clearSessionExpired,

    hasPermission,
    hasRoleLevel,
    isGlobalAdmin,
    isAdmin,
    canAccessClientResource,

    canManageClients,
    canManageUsers,
    canManageChatbots,
    canAccessGoogleIntegrations,
    canManageWhatsApp,
    canManageWhatsAppTemplates,
    canManageAutomations
  };
};

export default useAuth;
